import { useEffect, useState } from 'react';
import { UserPlus, Check, Copy } from 'lucide-react';
import { Button } from './Button.js';
import { isEmbedded } from '../discord/embedded.js';
import { inviteDialogAvailable, tryOpenInviteDialog } from '../discord/invite.js';
import { trackClient } from '../net/telemetry.js';

/**
 * InviteCTA — the lobby's "get more people in here" button. Production logs say a
 * majority of sessions are one person alone in a room nobody ever joins, and the
 * app had no invite affordance at all; this is it.
 *
 * Three tiers, degrading silently (never a dead button, never a throw):
 *   1. Inside Discord with the SDK's invite command → Discord's own invite sheet
 *      (`sdk.commands.openInviteDialog`, via discord/invite.ts).
 *   2. Anything else, or a rejected dialog (DM context / no CREATE_INSTANT_INVITE)
 *      → copy a share line to the clipboard.
 *   3. Clipboard blocked → in-place guidance.
 *
 * The share line is surface-specific and deliberately asymmetric. Outside Discord
 * it is a `/play?code=` join URL. Inside the Activity it carries NO room code and
 * NO URL: Activity rooms are keyed by Discord `instanceId`, there is no code-entry
 * screen anywhere on the Activity surface, and the `/play` URL would drag the
 * recipient out to the web app — a separate seat with no shared voice. The only
 * real way in is joining this call and launching the Activity, so that is what the
 * embedded copy says.
 *
 * `emphasis` picks the visual weight only: 'primary' for the lone host (inviting
 * is genuinely the only useful action), 'secondary' once others have joined.
 */
export function InviteCTA({
  code,
  emphasis = 'secondary',
  /** Optional max width so the button matches the surrounding column. */
  maxWidth,
  /**
   * Render as a fixed-height corner pill (white bg / black text, 40px tall,
   * self-sized — not `width: 100%`) instead of the full-width `Button`. Same
   * tiered click logic (Discord invite dialog → clipboard → manual reveal),
   * just a different shell for the top-right corner cluster
   * (ActivityGameLayout's lobby row).
   */
  compact,
  label,
  onActivated,
}: {
  code: string;
  emphasis?: 'primary' | 'secondary';
  maxWidth?: number;
  compact?: boolean;
  /** Optional idle-state label override (e.g. the stall nudge's "Summon your
   *  guild"). Confirmation states (copied / manual) keep their own copy. */
  label?: string;
  /** Fires alongside the `invite_opened` beacon whenever the CTA lands on any
   *  tier — lets a mount point (the stall nudge) attach its own telemetry
   *  without forking the tiered click logic. */
  onActivated?: () => void;
}) {
  const [state, setState] = useState<'idle' | 'copied' | 'manual'>('idle');

  // Let a copied/manual confirmation settle back to the idle label.
  useEffect(() => {
    if (state === 'idle') return;
    const t = setTimeout(() => setState('idle'), state === 'copied' ? 2400 : 8000);
    return () => clearTimeout(t);
  }, [state]);

  const embedded = isEmbedded();
  const shareText = embedded
    ? 'Ringmaster game starting — hop into this call and open the activity to join.'
    : `Join my Ringmaster game: ${shareUrl(code)}`;

  /** Any tier landing counts as "the user opened an invite" — see net/telemetry. */
  function activated(tier: 'dialog' | 'copied' | 'manual') {
    trackClient('invite_opened', { tier });
    onActivated?.();
  }

  async function onClick() {
    // Tier 1 — Discord's own invite dialog.
    if (inviteDialogAvailable() && (await tryOpenInviteDialog())) {
      activated('dialog');
      return;
    }
    // Tier 2 — clipboard. Tier 3 — reveal the code to read out loud.
    try {
      await navigator.clipboard.writeText(shareText);
      setState('copied');
      activated('copied');
    } catch {
      setState('manual');
      activated('manual');
    }
  }

  const shownLabel =
    state === 'copied'
      ? 'Copied — paste it in chat'
      : state === 'manual'
        ? embedded
          ? 'Ask them to join this call'
          : `Share room code ${code}`
        : (label ??
          (inviteDialogAvailable()
            ? 'Invite friends'
            : 'Copy invite'));

  const Icon = state === 'copied' ? Check : inviteDialogAvailable() ? UserPlus : Copy;

  if (compact) {
    return (
      <button type="button" className="rm-hover-pill" onClick={() => void onClick()} style={compactPill}>
        <Icon size={15} aria-hidden />
        <span>{shownLabel}</span>
      </button>
    );
  }

  return (
    <div style={{ width: '100%', maxWidth, margin: '0 auto' }}>
      <Button
        variant={emphasis}
        tone={emphasis === 'primary' ? 'gold' : undefined}
        onClick={() => void onClick()}
      >
        <Icon size={16} aria-hidden />
        <span>{shownLabel}</span>
      </Button>
    </div>
  );
}

const compactPill: React.CSSProperties = {
  height: 40,
  padding: '0 16px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  borderRadius: 'var(--radius-pill)',
  background: 'var(--btn-primary-bg)',
  color: 'var(--btn-primary-text)',
  border: 'none',
  fontFamily: 'var(--font-ui)',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  transition: 'filter var(--duration-fast, 160ms) var(--ease-standard), transform var(--duration-fast, 160ms) var(--ease-standard)',
};

/** The public join URL for a room — only meaningful outside the Discord iframe. */
function shareUrl(code: string): string {
  const origin = typeof window === 'undefined' ? '' : window.location.origin;
  return `${origin}/play?code=${code}`;
}
